class MoodSlider extends HTMLElement {
  static POSITIONS = ['is-far-prev', 'is-prev', 'is-active', 'is-next', 'is-far-next', 'is-hidden'];
  static LEFT_SIDE = new Set(['is-far-prev', 'is-prev']);
  static RIGHT_SIDE = new Set(['is-far-next', 'is-next']);

  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-index]')];
    if (this.slides.length === 0) return;

    this.activeIndex = Math.floor(this.slides.length / 2);
    this.touchStartX = 0;
    this.dragStartX = 0;
    this.isDragging = false;
    this.isSwiping = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    this.addEventListener('touchend', this.handleTouchEnd);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);

    for (const slide of this.slides) {
      slide.style.transition = 'none';
    }
    this.applyAllPositions();
    this.offsetHeight;
    for (const slide of this.slides) {
      slide.style.transition = '';
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('touchstart', this.handleTouchStart);
    this.removeEventListener('touchmove', this.handleTouchMove);
    this.removeEventListener('touchend', this.handleTouchEnd);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('mousedown', this.handleMouseDown);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  /* ---- Events ---- */

  handleClick(event) {
    // Arrows first: a drag can't start on them (see handleMouseDown), so
    // their clicks are never post-drag ghost clicks
    const prev = event.target.closest('.section-pdp-related__nav--prev');
    const next = event.target.closest('.section-pdp-related__nav--next');

    if (prev) { this.navigate(-1); return; }
    if (next) { this.navigate(1); return; }

    // Suppress only the single ghost click that follows a drag, then
    // clear the flag so later clicks work again
    if (this.isSwiping) {
      event.preventDefault();
      this.isSwiping = false;
      return;
    }

    const slide = event.target.closest('[data-index]');
    if (slide && !slide.classList.contains('is-active')) {
      event.preventDefault();
      this.activeIndex = parseInt(slide.dataset.index, 10);
      this.update();
    }
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.isSwiping = false;
  }

  handleTouchMove(event) {
    if (Math.abs(event.touches[0].clientX - this.touchStartX) > 10) {
      this.isSwiping = true;
    }
  }

  handleTouchEnd(event) {
    const delta = event.changedTouches[0].clientX - this.touchStartX;
    if (Math.abs(delta) > 40) {
      this.navigate(delta > 0 ? -1 : 1);
    }
  }

  handleMouseDown(event) {
    if (event.target.closest('.section-pdp-related__nav')) return;
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.isSwiping = false;
    this.classList.add('is-dragging');
    event.preventDefault();
  }

  handleMouseMove(event) {
    if (!this.isDragging) return;
    if (Math.abs(event.clientX - this.dragStartX) > 10) {
      this.isSwiping = true;
    }
  }

  handleMouseUp(event) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.classList.remove('is-dragging');
    const delta = event.clientX - this.dragStartX;
    if (Math.abs(delta) > 40) {
      this.navigate(delta > 0 ? -1 : 1);
    }
    // The ghost click (if any) dispatches right after mouseup; clear the
    // flag afterwards so it can't leak into later clicks
    setTimeout(() => { this.isSwiping = false; }, 0);
  }

  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(1);
    }
  }

  /* ---- Navigation ---- */

  navigate(direction) {
    if (this.slides.length <= 1) return;
    this.activeIndex = (this.activeIndex + direction + this.slides.length) % this.slides.length;
    this.update();
  }

  /* ---- Position helpers ---- */

  getWrappedOffset(index) {
    const total = this.slides.length;
    let offset = index - this.activeIndex;
    if (offset > total / 2) offset -= total;
    if (offset < -(total / 2)) offset += total;
    return offset;
  }

  positionForOffset(offset) {
    if (offset === 0) return 'is-active';
    if (offset === -1) return 'is-prev';
    if (offset === 1) return 'is-next';
    if (offset === -2) return 'is-far-prev';
    if (offset === 2) return 'is-far-next';
    return 'is-hidden';
  }

  /* ---- Update ---- */

  update() {
    const teleporting = [];

    for (const slide of this.slides) {
      const index = parseInt(slide.dataset.index, 10);
      const offset = this.getWrappedOffset(index);
      const newPos = this.positionForOffset(offset);
      const oldPos = MoodSlider.POSITIONS.find(cls => slide.classList.contains(cls));

      const crossesSides =
        (MoodSlider.LEFT_SIDE.has(oldPos) && MoodSlider.RIGHT_SIDE.has(newPos)) ||
        (MoodSlider.RIGHT_SIDE.has(oldPos) && MoodSlider.LEFT_SIDE.has(newPos));

      slide.classList.remove(...MoodSlider.POSITIONS);

      if (crossesSides) {
        slide.style.transition = 'none';
        slide.style.opacity = '0';
        slide.classList.add(newPos);
        teleporting.push(slide);
      } else {
        slide.classList.add(newPos);
      }
    }

    if (teleporting.length > 0) {
      this.offsetHeight;
      requestAnimationFrame(() => {
        for (const slide of teleporting) {
          slide.style.transition = '';
          slide.style.opacity = '';
        }
      });
    }

    this.updateButtons();
  }

  applyAllPositions() {
    for (const slide of this.slides) {
      const index = parseInt(slide.dataset.index, 10);
      const offset = this.getWrappedOffset(index);
      slide.classList.remove(...MoodSlider.POSITIONS);
      slide.classList.add(this.positionForOffset(offset));
    }
    this.updateButtons();
  }

  updateButtons() {
    const prevBtn = this.querySelector('.section-pdp-related__nav--prev');
    const nextBtn = this.querySelector('.section-pdp-related__nav--next');
    const canNavigate = this.slides.length > 1;
    if (prevBtn) prevBtn.disabled = !canNavigate;
    if (nextBtn) nextBtn.disabled = !canNavigate;
  }
}

if (!customElements.get('mood-slider')) {
  customElements.define('mood-slider', MoodSlider);
}
